import { useState } from "react";
import { Phone, Mail, MessageSquare, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import axios from "axios";

export const ContactSection = () => {
  const { services, settings, API } = useSite();
  const phone = settings?.phone || "514-775-5027";
  const email = settings?.email || "lesentretienselites@gmail.com";

  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", services: [], message: "", contact_preference: "phone" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleService = (name) => {
    setForm((p) => ({
      ...p,
      services: p.services.includes(name) ? p.services.filter((s) => s !== name) : [...p.services, name],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || form.services.length === 0) {
      toast.error("Veuillez remplir votre nom, téléphone et sélectionner au moins un service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSubmitted(true);
      toast.success("Demande envoyée avec succès!");
    } catch {
      toast.error("Erreur. Veuillez réessayer ou nous appeler directement.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="contact" className="bg-[#F5F5F0] py-16 md:py-24" data-testid="contact-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white rounded-xl p-10 border">
            <CheckCircle className="w-16 h-16 text-[#2D5A3D] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Merci pour votre demande!</h2>
            <p className="text-gray-600 mb-6">Nous vous contacterons très bientôt. En attendant, n'hésitez pas à nous appeler.</p>
            <a href={`tel:${phone.replace(/-/g, "")}`} className="inline-flex items-center gap-2 bg-[#2D5A3D] text-white px-6 py-3 rounded-md font-medium hover:bg-[#234A30] transition-colors">
              <Phone className="w-4 h-4" />{phone}
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="bg-[#F5F5F0] py-16 md:py-24" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Left - Info */}
          <div className="lg:col-span-2">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="contact-title">
              Obtenez votre soumission gratuite
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Remplissez le formulaire ou contactez-nous directement. Nous répondons rapidement.
            </p>
            <div className="space-y-3">
              <a href={`tel:${phone.replace(/-/g, "")}`} className="flex items-center gap-3 p-4 bg-white rounded-lg hover:bg-green-50 transition-colors border border-gray-100" data-testid="contact-phone-link">
                <div className="w-10 h-10 rounded-full bg-[#2D5A3D] flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-white" /></div>
                <div><p className="font-medium text-gray-900">Appelez-nous</p><p className="text-sm text-gray-500">{phone}</p></div>
              </a>
              <a href={`sms:${phone.replace(/-/g, "")}`} className="flex items-center gap-3 p-4 bg-white rounded-lg hover:bg-green-50 transition-colors border border-gray-100" data-testid="contact-sms-link">
                <div className="w-10 h-10 rounded-full bg-[#2D5A3D] flex items-center justify-center flex-shrink-0"><MessageSquare className="w-5 h-5 text-white" /></div>
                <div><p className="font-medium text-gray-900">Envoyez un texto</p><p className="text-sm text-gray-500">{phone}</p></div>
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-3 p-4 bg-white rounded-lg hover:bg-green-50 transition-colors border border-gray-100" data-testid="contact-email-link">
                <div className="w-10 h-10 rounded-full bg-[#2D5A3D] flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-white" /></div>
                <div><p className="font-medium text-gray-900">Écrivez-nous</p><p className="text-sm text-gray-500">{email}</p></div>
              </a>
            </div>
          </div>

          {/* Right - Form */}
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white rounded-xl p-6 md:p-8 border border-gray-100 shadow-sm" data-testid="contact-form">
              <h3 className="font-semibold text-lg text-gray-900 mb-5">Formulaire de soumission rapide</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet <span className="text-red-500">*</span></label>
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Votre nom" data-testid="form-name" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone <span className="text-red-500">*</span></label>
                    <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="514-000-0000" type="tel" data-testid="form-phone" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Courriel</label>
                    <Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="votre@courriel.com" type="email" data-testid="form-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ville / Secteur</label>
                    <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Ex: Châteauguay" data-testid="form-city" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Services demandés <span className="text-red-500">*</span></label>
                  <div className="flex flex-wrap gap-2" data-testid="form-services">
                    {services.map((s) => (
                      <button key={s.id} type="button" onClick={() => toggleService(s.name)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${form.services.includes(s.name) ? "bg-[#2D5A3D] text-white border-[#2D5A3D]" : "bg-white text-gray-700 border-gray-300 hover:border-[#2D5A3D]"}`}
                        data-testid={`form-service-${s.id}`}>
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Détails (optionnel)</label>
                  <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Décrivez brièvement vos besoins..." rows={3} className="resize-none" data-testid="form-message" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Préférence de contact</label>
                  <div className="flex gap-3" data-testid="form-contact-pref">
                    {[{ v: "phone", l: "Téléphone" }, { v: "text", l: "Texto" }, { v: "email", l: "Courriel" }].map((o) => (
                      <button key={o.v} type="button" onClick={() => set("contact_preference", o.v)}
                        className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${form.contact_preference === o.v ? "bg-[#2D5A3D] text-white border-[#2D5A3D]" : "bg-white text-gray-700 border-gray-300"}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-[#2D5A3D] hover:bg-[#234A30] text-white h-12 text-base" data-testid="form-submit-button">
                  {loading ? "Envoi en cours..." : <><Send className="w-4 h-4 mr-2" />Demander une soumission gratuite</>}
                </Button>
                <p className="text-xs text-gray-500 text-center">100% gratuit · Sans engagement · Réponse rapide</p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
